// =============================================================================
// Seat.tsx — Player seat component on the poker table
// Project: Texas Hold'em Social Casino
// =============================================================================
// Shows player avatar, name, chip count, hole cards, and dealer button.
// Highlights the active/current turn player with a pulsing border.

import React from 'react'
import { Player } from '../types'
import CardComponent from './Card'

interface SeatProps {
  player: Player
  x: number   // Percentage position (0-100)
  y: number
  isMe: boolean
  isCurrent: boolean
  isDealer: boolean
  mySeatIndex: number
  totalPlayers: number
}

export default function SeatComponent({ player, x, y, isMe, isCurrent, isDealer }: SeatProps): React.ReactElement {
  // Position from table center, accounting for felt radius
  const style: React.CSSProperties = {
    left: `${x}%`,
    top: `${y}%`,
    transform: 'translate(-50%, -50%)',
  }

  return (
    <div className={`seat ${isCurrent ? 'seat-active' : ''}`} style={style}>
      {/* Dealer button */}
      {isDealer && (
        <div style={{
          width: 22, height: 22, borderRadius: '50%',
          background: '#f5c518', color: '#000',
          fontSize: 10, fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'absolute', top: -10, left: -10,
          zIndex: 5,
        }}>
          D
        </div>
      )}

      {/* Avatar */}
      <div className="seat-avatar" style={{ borderColor: isMe ? '#e8491d' : '#f5c518' }}>
        {player.isAI ? (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, color: '#f5c518',
          }}>
            🤖
          </div>
        ) : (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, color: '#e0e0e0',
          }}>
            {isMe ? '👤' : '👤'}
          </div>
        )}
      </div>

      {/* Name */}
      <div className="seat-name" style={{ color: isMe ? '#e8491d' : '#e0e0e0' }}>
        {isMe ? 'You' : player.name}
      </div>

      {/* Chips */}
      <div className="seat-chips">
        {player.chips.toLocaleString()}
      </div>

      {/* Current bet */}
      {player.currentBet > 0 && (
        <div style={{
          position: 'absolute', bottom: -18,
          background: 'rgba(232, 73, 29, 0.8)',
          color: '#fff', fontSize: 10, fontWeight: 700,
          padding: '2px 8px', borderRadius: 8,
        }}>
          {player.currentBet}
        </div>
      )}

      {/* Hole cards */}
      {player.holeCards && (
        <div style={{ display: 'flex', gap: 3, marginTop: 2 }}>
          <CardComponent card={player.holeCards[0]} size="small" />
          <CardComponent card={player.holeCards[1]} size="small" />
        </div>
      )}

      {/* Folded indicator */}
      {player.folded && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, color: 'rgba(255,0,0,0.5)',
        }}>
          ✕
        </div>
      )}
    </div>
  )
}
